import PropTypes from 'prop-types';
import React, { useRef, useEffect, useCallback, memo } from 'react';
import { Map, List } from 'immutable';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import {
  changeName,
  changeFile,
  upload,
  clearValidationError,
  validate,
  reset,
} from '../../actions/project/templatePopup';
import { showInformation } from '../../utils/MessagePopup';
import { usePopupContext } from '../../contexts/PopupContext';

const TemplatePopupContainer = ({
  children,
  isUploading,
  stage,
  name,
  inputErrors,
  projectId,
  onClose,
  changeName,
  changeFile,
  upload,
  clearValidationError,
  validate,
  reset,
  ...rest
}) => {
  const { closePopup, openPopup, onClosePopup } = usePopupContext();
  const formRef = useRef(null);

  useEffect(
    () => () => {
      reset();
    },
    [],
  );

  useEffect(() => {
    if (onClose) {
      onClosePopup(onClose);
    }
  }, [onClose, onClosePopup]);

  useEffect(() => {
    if (stage === 'done') {
      if (onClose) {
        reset();
        showInformation(openPopup, 'Uploaded successfully!', onClose);
        onClosePopup(onClose);
      } else {
        closePopup();
        reset();
        showInformation(openPopup, 'Uploaded successfully!');
      }
    } else if (stage === 'error') {
      closePopup();
    }
  }, [stage, onClose, closePopup, openPopup, onClosePopup]);

  const handleUpload = useCallback(() => {
    if (isUploading) return;

    if (stage === 'start' || stage === 'invalid') {
      validate();

      return;
    }

    if (stage === 'ready') {
      upload(projectId, new FormData(formRef.current));
    }
  }, [isUploading, stage, validate, upload, projectId]);

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleChange = useCallback(
    event => {
      changeName(event.target.value);
    },
    [changeName],
  );

  const handleChoose = useCallback(
    event => {
      changeFile(event.target.files[0].name || '');
    },
    [changeFile],
  );

  const handleErrorClose = useCallback(
    (_, field) => {
      clearValidationError(field);
    },
    [clearValidationError],
  );

  const setFormElement = useCallback(input => {
    formRef.current = input;
  }, []);

  const nameError = name.length < 255 ? null : 'Name is too long';
  const computedInputErrors = nameError ? inputErrors.set('name', nameError) : inputErrors;

  return (
    <>
      <Popups.UploadPopup
        {...rest}
        disabledUpload={isUploading}
        hasErrors={computedInputErrors.size > 0}
        inputErrors={computedInputErrors}
        mimeType=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        name={name}
        onChange={handleChange}
        onChoose={handleChoose}
        onClose={handleClose}
        onErrorClose={handleErrorClose}
        onUpload={handleUpload}
        setFormElement={setFormElement}
      />
      {children}
    </>
  );
};

TemplatePopupContainer.propTypes = {
  children: PropTypes.node,
  errors: PropTypes.instanceOf(List),
  file: PropTypes.object,
  inputErrors: PropTypes.instanceOf(Map),
  isUploading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  projectId: PropTypes.number.isRequired,
  stage: PropTypes.string.isRequired,
};

const mapStateToProps = (state, props) => {
  const popupState = state.project.templatePopup;

  return {
    projectId: props.popup.getIn(['props', 'projectId']),
    onClose: props.popup.getIn(['props', 'onClose']),
    name: popupState.get('name'),
    inputErrors: popupState.get('inputErrors', Map()),
    isUploading: popupState.get('isUploading'),
    stage: popupState.get('stage'),
    errors: popupState.get('errors'),
  };
};

export { TemplatePopupContainer };
export default connect(
  mapStateToProps,
  {
    changeName,
    changeFile,
    upload,
    clearValidationError,
    validate,
    reset,
  },
  mergeProps,
  connectOptions,
)(memo(TemplatePopupContainer));
